import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Button } from 'primeng/button';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioFormBase } from '@/shared/components/relatorios/relatorio-form.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { DeclaracaoSubstituicaoDiretorTecnicoPjService } from '@/core/api/sicom-api/registro/prestador/relatorios/declaracao-substituicao-diretor-tecnico-pj/declaracao-substituicao-diretor-tecnico-pj.service';
import {
    ReportDeclaracaoSubstituicaoDiretorTecnicoPjRequest,
    ReportDeclaracaoSubstituicaoDiretorTecnicoPjResponse
} from '@/core/api/sicom-api/registro/prestador/relatorios/declaracao-substituicao-diretor-tecnico-pj/declaracao-substituicao-diretor-tecnico-pj.model';
import { DeclaracaoSubstituicaoDiretorTecnicoPjJobService } from '@/core/services/declaracao-substituicao-diretor-tecnico-pj-job.service';
import { PessoaService } from '@/shared/services/pessoa.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

type Req = ReportDeclaracaoSubstituicaoDiretorTecnicoPjRequest;
type Resp = ReportDeclaracaoSubstituicaoDiretorTecnicoPjResponse;

/**
 * Relatório PDF assíncrono: Declaração de Substituição de Diretor Técnico (prestador PJ).
 * Herda `RelatorioFormBase`: export/polling/download/erro vêm da base; aqui só resolvemos a
 * identificação do prestador (via `PessoaService`) — apenas nuCrm/sgUf, sem filtros adicionais.
 * O documento é preenchido manualmente pelo novo diretor técnico após a impressão
 * (nome/número de registro e assinaturas ficam em branco).
 */
@Component({
    selector: 'app-declaracao-substituicao-diretor-tecnico-pj',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, RelatorioContainer, CanDirective],
    templateUrl: './declaracao-substituicao-diretor-tecnico-pj.html',
    styleUrl: './declaracao-substituicao-diretor-tecnico-pj.scss'
})
export class ReportDeclaracaoSubstituicaoDiretorTecnicoPjComponent extends RelatorioFormBase<Req, Resp> {
    /** Gate do botao Gerar: o backend exige esta role em /reports/** do modulo. */
    protected override readonly roleGerar = PERMISSOES.prestadores.relatorios.gerar;

    private readonly declaracaoService = inject(DeclaracaoSubstituicaoDiretorTecnicoPjService);
    private readonly pessoaService = inject(PessoaService);
    readonly jobService = inject(DeclaracaoSubstituicaoDiretorTecnicoPjJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.jobService.job$;

    protected get jobFacade(): RelatorioJobFacade {
        return this.jobService;
    }

    /** Monta o request a partir do prestador selecionado; retorna null (e avisa) se faltar CRM/UF. */
    protected buildRequest(): Req | null {
        const pessoa = this.pessoaService.getPessoa();
        const pessoaReference = this.pessoaService.getPessoaReference();
        const nuCrm = Number(pessoa?.dadosGerais?.crm || pessoa?.crm || pessoa?.registro || 0);
        const sgUf = pessoa?.uf || pessoaReference?.uf || '';

        if (!nuCrm || !sgUf) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Selecione um prestador antes de gerar a declaração.'
            });
            return null;
        }

        return { nuCrm, sgUf };
    }

    protected exportar(request: Req) {
        return this.declaracaoService.exportarPdf(request);
    }

    limparFormulario(): void {
        this.limparJob();
    }
}
